import { opusClient } from "@/utils/rest-client/opus-client";
import {
	CreatedUserDetailsDto,
	CreatedUserRelationshipDto,
} from "@/utils/validators/users.validator";
import { CodeResponse, googleLogout } from "@react-oauth/google";
import { useTsRestQueryClient } from "@ts-rest/react-query";
import storage from "@/utils/api/storage";
import { ReactNode, createContext, useEffect, useState } from "react";
// import useCurrentLocation from "@/hooks/useCurrentLocation";
import useGeolocation from "@/hooks/useGeolocation";
import { useGeolocated } from "react-geolocated";

interface ILocation {
	current_location: string;
	country: string;
	lng: string;
	lat: string;
}
interface IContext {
	user: CreatedUserDetailsDto | null;
	accessToken: string | null;
	login: (response: CodeResponse) => Promise<any> | void;
	loginEmail: (email: string, password: string) => void;
	logout: () => void;
	getMe: () => Promise<any> | void;
	getUsers: () => Promise<any> | void;
	users: CreatedUserRelationshipDto[] | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	location: ILocation;
	setLocation: (x: ILocation) => void;
	setIsLoading: (x: boolean) => void;
	setAccessToken: (x: string | null) => void;
	setRefreshToken: (x: string | null) => void;
	isLoginError: boolean;
	isGoogleLoginError: boolean;
	errorMsg: unknown;
}
const AuthContext = createContext<IContext>({
	user: null,
	accessToken: null,
	login: () => { },
	logout: () => { },
	loginEmail: () => { },
	getMe: () => { },
	getUsers: () => { },
	users: [],
	isAuthenticated: false,
	isLoading: true,
	location: {
		current_location: "-",
		country: "-",
		lng: "-",
		lat: "-",
	},
	setLocation: () => { },
	setIsLoading: () => { },
	setAccessToken: () => { },
	setRefreshToken: () => { },
	isLoginError: false,
	isGoogleLoginError: false,
	errorMsg: {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const apiQueryClient = useTsRestQueryClient(opusClient);

	const [user, setUser] = useState<CreatedUserDetailsDto | null>(null);
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
	const [accessToken, setAccessToken] = useState<any>();
	const [, setRefreshToken] = useState<string | null>(null);
	const [location, setLocation] = useState<ILocation>({
		current_location: "-",
		country: "-",
		lng: "-",
		lat: "-",
	});
	// const [currenLocation, setCurrentLocation] = useState({
	// 	lng: "-",
	// 	lat: "-",
	// });
	const { coords } = useGeolocated({
		positionOptions: {
			enableHighAccuracy: false,
		},
		userDecisionTimeout: 5000,
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	const {
		data: meData,
		refetch: getMe,
		isLoading: isMeLoading,
		isError: isMeError,
	} = apiQueryClient.auth.me.useQuery(["me"], undefined, {
		// Only fetch when access token is available

		enabled: !!accessToken,
		queryKey: ["me"],
	});

	// const { data: dataLocation } = useCurrentLocation();
	const { data: dataHome } = useGeolocation(
		Number(coords?.latitude),
		Number(coords?.longitude)
	);

	const usersListQuery = {
		include: {
			profilePicture: true,
			role: true,
			userStatus: true,
			profile: true,
		},
	};

	const { data: userList, refetch } = apiQueryClient.users.getUsers.useQuery(
		["users", usersListQuery],
		{
			query: usersListQuery,
		},
		{
			enabled: !!accessToken,
			queryKey: ["users"],
		}
	);

	const setInitialUserAccess = (user: CreatedUserDetailsDto) => {
		if (user.role.title === "Admin") {
			storage.setItem("accessStatus", "Admin");
		} else if (user.isLeaveApprover) {
			storage.setItem("accessStatus", "Approver");
		} else {
			storage.setItem("accessStatus", "Staff");
		}
	};

	const {
		mutate: googleLogin,
		isPending: isGoogleLoading,
		isError: isGoogleError,
		error: errorGoogleMsg,
	} = apiQueryClient.auth.google.useMutation({
		onSuccess: async ({ body }) => {
			storage.setItem("accessToken", body.accessToken);
			storage.setItem("refreshToken", body.refreshToken);
			setAccessToken(body.accessToken);
			setRefreshToken(body.refreshToken);

			const resp = await getMe();
			if (resp.data?.body) setInitialUserAccess(resp.data.body)
			window.location.href = "/dashboard";
		},
		onError: () => {
			logout();
			// window.location.href = "/";
		},
	});

	const {
		mutate: emailLogin,
		isPending: isLoginLoading,
		isError: isLoginError,
		error: errorLoginMsg,
	} = apiQueryClient.auth.emailLogin.useMutation({
		onSuccess: async ({ body }) => {
			storage.setItem("accessToken", body.accessToken);
			storage.setItem("refreshToken", body.refreshToken);
			setAccessToken(body.accessToken);
			setRefreshToken(body.refreshToken);

			const resp = await getMe();
			if (resp.data?.body) setInitialUserAccess(resp.data.body)
			window.location.href = "/dashboard";
		},
		onError: (error) => {
			console.log(error);
			// logout();
			// window.location.href = "/";
		},
	});

	const login = async (response: CodeResponse) => {
		googleLogin({
			body: {
				auth: {
					scheme: "google",
					code: response.code,
				},
				timezone: timeZone,
			},
		});
	};

	const loginEmail = async (email: string, password: string) => {
		emailLogin({
			body: {
				auth: {
					scheme: "email",
					email: email,
					password: password,
				},
				timezone: timeZone,
			},
		});
	};

	// const getLocation = () => {
	// 	if (navigator.geolocation) {
	// 		navigator.permissions
	// 			.query({ name: "geolocation" })
	// 			.then(function (result) {
	// 				if (result.state === "granted") {
	// 					navigator.geolocation.getCurrentPosition(
	// 						(pos) => {
	// 							console.log(pos);
	// 							setCurrentLocation({
	// 								lat: Number(pos.coords.latitude).toFixed(2),
	// 								lng: Number(pos.coords.longitude).toFixed(2),
	// 							});
	// 						},
	// 						(err) => {
	// 							console.log(err);
	// 						}
	// 					);
	// 				} else if (result.state === "prompt") {
	// 					navigator.geolocation.getCurrentPosition(
	// 						(pos) => {
	// 							setCurrentLocation({
	// 								lat: Number(pos.coords.latitude).toFixed(2),
	// 								lng: Number(pos.coords.longitude).toFixed(2),
	// 							});
	// 						},
	// 						(err) => {
	// 							console.log(err);
	// 						}
	// 					);
	// 				} else if (result.state === "denied") {
	// 					// console.log(result);
	// 				}
	// 			});
	// 	} else {
	// 		console.log("Geolocation is not supported by this browser.");
	// 	}
	// };

	const logout = () => {
		if (accessToken) {
			googleLogout();
			storage.removeItem("accessToken");
			storage.removeItem("refreshToken");
			setAccessToken(null);
			setRefreshToken(null);
			setUser(null);
			setIsAuthenticated(false);
			if (storage.getItem("accessStatus")) {
				storage.removeItem("accessStatus");
			}
		}
	};

	// useEffect(()=> {
	// 	navigator.geolocation.getCurrentPosition(function (position) {
	// 		console.log("Latitude is :", position.coords.latitude);
	// 		console.log("Longitude is :", position.coords.longitude);
	// 	});
	// },[])

	useEffect(() => {
		// if (dataLocation) {
		// 	if (dataLocation.status === 403) {
		// 		getLocation();
		// 		if (dataHome) {
		// 			setLocation({
		// 				current_location: dataHome.address.city
		// 					? dataHome.address.city
		// 					: dataHome.address.region,
		// 				country: dataHome.address.country,
		// 				lat: Number(currenLocation.lat).toFixed(2),
		// 				lng: Number(currenLocation.lng).toFixed(2),
		// 			});
		// 		}
		// 	} else {
		// 		setLocation({
		// 			current_location: dataLocation.city
		// 				? dataLocation.city
		// 				: dataLocation.region,
		// 			country: dataLocation.country_name,
		// 			lat: Number(dataLocation.latitude).toFixed(2),
		// 			lng: Number(dataLocation.longitude).toFixed(2),
		// 		});
		// 	}
		// } else {
		// 	getLocation();
		// 	if (dataHome) {
		// 		setLocation({
		// 			current_location: dataHome.address.city
		// 				? dataHome.address.city
		// 				: dataHome.address.region,
		// 			country: dataHome.address.country,
		// 			lat: Number(currenLocation.lat).toFixed(2),
		// 			lng: Number(currenLocation.lng).toFixed(2),
		// 		});
		// 	}
		// }
		if (dataHome) {
			setLocation({
				current_location: dataHome.address.city
					? dataHome.address.city
					: dataHome.address.region,
				country: dataHome.address.country,
				lat: Number(coords?.latitude).toFixed(2),
				lng: Number(coords?.longitude).toFixed(2),
			});
		}
	}, [dataHome]);

	useEffect(() => {
		if (storage.getItem("accessToken") && storage.getItem("refreshToken")) {
			setAccessToken(storage.getItem("accessToken"));
			setRefreshToken(storage.getItem("refreshToken"));
		}
	}, [storage]);

	useEffect(() => {
		if (meData?.status == 200) {
			setUser(meData.body);
			setIsAuthenticated(true);
		}
		if (isMeError) {
			logout();
		}
	}, [meData]);

	return (
		<AuthContext.Provider
			value={{
				user,
				accessToken,
				login,
				logout,
				loginEmail,
				getMe,
				getUsers: refetch,
				users: userList?.body.data ?? null,
				isAuthenticated,
				isLoading:
					isGoogleLoading || isMeLoading || isLoading || isLoginLoading,
				location,
				setLocation,
				setIsLoading,
				setAccessToken,
				setRefreshToken,
				isLoginError: isLoginError,
				isGoogleLoginError: isGoogleError,
				errorMsg: errorGoogleMsg || errorLoginMsg,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export default AuthContext;
